import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4MoldBedBugsArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Mold, Bed Bugs, or Pest Damage? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Find out whether renters insurance covers mold, bed bugs, and pest damage, what is usually excluded, and who is responsible as a Texas renter.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-mold-bed-bugs-pest-damage"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover mold, bed bugs, or pest damage?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Mold, bed bugs, and pest infestations are common renter concerns — but HO-4 policies handle them very
              differently from fire or theft. Here is what to expect.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Renters insurance <strong>generally does not cover</strong> mold, bed bugs, or routine pest infestations.
                These are usually considered maintenance issues, not sudden accidental losses. Mold or pest damage{' '}
                <em>resulting from</em> a covered event (like a burst pipe) may be partially covered — but the underlying
                mold or infestation itself typically is not.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Coverage breakdown by issue</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Issue</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Usually covered?</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Mold from long-term humidity</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Gradual damage / maintenance exclusion</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Mold after covered water leak</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Sometimes</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>May cover belongings damaged by the leak; mold remediation often limited</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Bed bug infestation</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Treated as vermin/infestation exclusion on most policies</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Rodent or insect damage</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Landlord typically responsible for pest control in most leases</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Belongings destroyed by covered fire/smoke</td>
                      <td style={{ padding: '0.6rem' }}>Yes</td>
                      <td style={{ padding: '0.6rem' }}>Personal property coverage applies regardless of mold/bed bug exclusions</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Mold and renters insurance</h3>
              <p>
                Most HO-4 policies exclude mold that develops over time from poor ventilation, humidity, or neglected
                maintenance. If a{' '}
                <Link to="/knowledge-base/does-ho4-cover-water-damage" style={articleLinkStyle}>sudden covered water event</Link>{' '}
                damages your belongings and mold develops as a result, your insurer may cover the original water damage
                to personal property but deny separate mold remediation costs. Read your policy&apos;s mold sub-limit —
                some policies cap mold coverage at $5,000–$10,000 even when partially covered.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Bed bugs and pest infestations</h3>
              <p>
                Bed bug treatment, extermination, and replacement of infested furniture are almost never covered by
                standard renters insurance. Texas law places pest control responsibility primarily on{' '}
                <strong>landlords</strong> unless the lease states otherwise or the tenant caused the infestation.
                Document the issue in writing, notify your landlord immediately, and keep photos as evidence.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Who is responsible? Renter vs. landlord</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Landlord</strong> — structural issues, pre-existing mold, building-wide pest problems, and
                  habitability repairs under Texas Property Code.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Tenant</strong> — damage you cause through negligence, and belongings damaged by uncovered
                  perils. Your{' '}
                  <Link to="/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance" style={articleLinkStyle}>personal property coverage</Link>{' '}
                  helps with your items after covered events.
                </li>
                <li>
                  <strong>Neither insurance nor landlord</strong> — gradual mold from unreported leaks, or bed bugs from
                  used furniture you introduced without inspection.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What you can do as a renter</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Report mold, leaks, and pests to your landlord in writing immediately.</li>
                <li style={{ marginBottom: '0.5rem' }}>Use exhaust fans and dehumidifiers to reduce humidity-related mold risk.</li>
                <li style={{ marginBottom: '0.5rem' }}>Inspect used furniture for bed bugs before bringing it into your unit.</li>
                <li>Carry HO-4 for covered perils like fire, theft, and sudden water damage — see{' '}
                  <Link to="/knowledge-base/is-ho4-worth-it" style={articleLinkStyle}>is HO-4 worth it</Link>.
                </li>
              </ol>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage for the perils renters insurance is designed to handle — fire, theft, liability, and more.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Unsure what your policy covers?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can review exclusions for mold, pests, and water damage on your specific policy.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4MoldBedBugsArticlePage;
